/**
 * Custom hook for managing file uploads.
 *
 * Handles file selection, validation, upload state tracking,
 * and progress reporting.
 */

import { useState, useCallback } from 'react';
import {
  FileAttachment,
  DEFAULT_UPLOAD_CONFIG,
  getFileCategory,
} from '../types/upload';
import {
  validateFile,
  generateFileId,
  createImagePreview,
} from '../utils/fileValidation';
import { uploadSingleFile } from '../services/upload/uploadService';

export interface UseFileUploadOptions {
  projectId: string;
  maxFiles?: number;
  onUploadComplete?: (attachment: FileAttachment) => void;
  onUploadError?: (attachment: FileAttachment, error: Error) => void;
  onValidationError?: (errors: string[]) => void;
}

export interface UseFileUploadReturn {
  /** Currently attached files */
  attachments: FileAttachment[];
  /** Add new files (validates and creates previews) */
  addFiles: (files: File[]) => Promise<void>;
  /** Remove a file by ID */
  removeFile: (id: string) => void;
  /** Clear all attachments */
  clearAll: () => void;
  /** Upload all pending files and return file IDs */
  uploadAll: () => Promise<string[]>;
  /** Whether any files are currently uploading */
  isUploading: boolean;
  /** Overall upload progress (0-100) */
  uploadProgress: number;
  /** Whether any uploads have errors */
  hasErrors: boolean;
  /** Whether more files can be attached */
  canAttach: boolean;
  /** Number of attached files */
  attachmentCount: number;
}

/**
 * Hook for managing file uploads with state tracking.
 */
export function useFileUpload({
  projectId,
  maxFiles = DEFAULT_UPLOAD_CONFIG.maxFilesPerMessage,
  onUploadComplete,
  onUploadError,
  onValidationError,
}: UseFileUploadOptions): UseFileUploadReturn {
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);

  // Computed state
  const isUploading = attachments.some((a) => a.uploadStatus === 'uploading');
  const hasErrors = attachments.some((a) => a.uploadStatus === 'error');
  const canAttach = attachments.length < maxFiles;

  // Calculate overall progress
  const uploadProgress =
    attachments.length > 0
      ? Math.round(
          attachments.reduce((sum, a) => sum + a.uploadProgress, 0) /
            attachments.length
        )
      : 0;

  /**
   * Add new files to the attachment list.
   * Validates files and creates previews for images.
   */
  const addFiles = useCallback(
    async (files: File[]) => {
      const newAttachments: FileAttachment[] = [];
      const errors: string[] = [];

      // Check remaining capacity
      const remainingSlots = maxFiles - attachments.length;
      if (files.length > remainingSlots) {
        errors.push(
          `Can only add ${remainingSlots} more file(s). ${files.length - remainingSlots} file(s) were not added.`
        );
        files = files.slice(0, remainingSlots);
      }

      for (const file of files) {
        // Validate file
        const validation = validateFile(file);
        if (!validation.valid) {
          if (validation.error) {
            errors.push(validation.error);
          }
          continue;
        }

        // Create attachment object
        const category = getFileCategory(file.type);
        if (category === 'unknown') {
          errors.push(`File type "${file.type}" is not supported.`);
          continue;
        }

        const attachment: FileAttachment = {
          id: generateFileId(),
          file,
          name: file.name,
          size: file.size,
          mimeType: file.type,
          category,
          uploadProgress: 0,
          uploadStatus: 'pending',
        };

        // Create preview for images
        if (category === 'image') {
          try {
            attachment.preview = await createImagePreview(file);
          } catch {
            // Preview failed, but we can still upload
            console.warn(`Failed to create preview for ${file.name}`);
          }
        }

        newAttachments.push(attachment);
      }

      // Report validation errors
      if (errors.length > 0 && onValidationError) {
        onValidationError(errors);
      }

      // Add new attachments to state
      if (newAttachments.length > 0) {
        setAttachments((prev) => [...prev, ...newAttachments]);
      }
    },
    [attachments.length, maxFiles, onValidationError]
  );

  /**
   * Remove a file from the attachment list.
   */
  const removeFile = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  /**
   * Clear all attachments.
   */
  const clearAll = useCallback(() => {
    setAttachments([]);
  }, []);

  /**
   * Upload all pending files.
   * Returns array of file IDs for successfully uploaded files.
   */
  const uploadAll = useCallback(async (): Promise<string[]> => {
    const pendingAttachments = attachments.filter(
      (a) => a.uploadStatus === 'pending' || a.uploadStatus === 'error'
    );

    if (pendingAttachments.length === 0) {
      // Return IDs of already uploaded files
      return attachments
        .filter((a) => a.uploadStatus === 'uploaded' && a.fileId)
        .map((a) => a.fileId!);
    }

    const uploadedFileIds: string[] = [];

    // Upload each file individually for progress tracking
    for (const attachment of pendingAttachments) {
      // Update status to uploading
      setAttachments((prev) =>
        prev.map((a) =>
          a.id === attachment.id
            ? { ...a, uploadStatus: 'uploading' as const, uploadProgress: 0 }
            : a
        )
      );

      try {
        const response = await uploadSingleFile(
          projectId,
          attachment.file,
          (progress) => {
            setAttachments((prev) =>
              prev.map((a) =>
                a.id === attachment.id ? { ...a, uploadProgress: progress } : a
              )
            );
          }
        );

        // Update with success
        const updatedAttachment: FileAttachment = {
          ...attachment,
          uploadStatus: 'uploaded',
          uploadProgress: 100,
          fileId: response.file_id,
          gcsUri: response.gcs_uri,
        };

        setAttachments((prev) =>
          prev.map((a) => (a.id === attachment.id ? updatedAttachment : a))
        );

        uploadedFileIds.push(response.file_id);

        if (onUploadComplete) {
          onUploadComplete(updatedAttachment);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Upload failed');

        // Update with error
        const errorAttachment: FileAttachment = {
          ...attachment,
          uploadStatus: 'error',
          uploadProgress: 0,
          errorMessage: error.message,
        };

        setAttachments((prev) =>
          prev.map((a) => (a.id === attachment.id ? errorAttachment : a))
        );

        if (onUploadError) {
          onUploadError(errorAttachment, error);
        }
      }
    }

    // Also include already uploaded files
    const existingFileIds = attachments
      .filter(
        (a) =>
          a.uploadStatus === 'uploaded' &&
          a.fileId &&
          !pendingAttachments.some((p) => p.id === a.id)
      )
      .map((a) => a.fileId!);

    return [...existingFileIds, ...uploadedFileIds];
  }, [attachments, projectId, onUploadComplete, onUploadError]);

  return {
    attachments,
    addFiles,
    removeFile,
    clearAll,
    uploadAll,
    isUploading,
    uploadProgress,
    hasErrors,
    canAttach,
    attachmentCount: attachments.length,
  };
}

export default useFileUpload;
